// The Activity page's timeline: who did what, and every alert, built from the
// rows that already record it (`repo.reports.activity`). Nothing is stored for
// this page. It is about people and service, not stock going in and out, which
// stays on the Stock page (docs/plans/activity.md).
import { GUEST_FREE_WAIT_MS, formatQty, mulDivRound } from '@ims/core'
import type { ActivityRows, InvoiceRow, OrderRow, PaymentMethod } from '@ims/data'
import { isBatchMove } from './batches.ts'
import { orderName, ticketName } from './orders.ts'

/** The pills on the page. */
export type ActivityGroup = 'orders' | 'kitchen' | 'payments' | 'stock' | 'alerts'

export type ActivityKind =
  | 'order_opened'
  | 'dish_cancelled'
  | 'order_cancelled'
  | 'order_merged'
  | 'checked_out'
  | 'ticket_ready'
  | 'request_answered'
  | 'request_waiting'
  | 'request_missed'
  | 'delivery_out'
  | 'delivered'
  | 'delivery_failed'
  | 'bill_made'
  | 'bill_cancelled'
  | 'credit_note'
  | 'bill_reprinted'
  | 'payment'
  | 'payment_qr'
  | 'purchase'
  | 'stock_adjusted'

/** A piece of an event's sentence; `strong` pieces are the names in it. */
export type Part = string | { strong: string }

export interface ActivityEvent {
  id: string
  /** When it happened (ISO). */
  at: string
  group: ActivityGroup
  kind: ActivityKind
  /** Who did it, when a user is recorded for it. The screen puts the name first. */
  actorId: string | null | undefined
  /** The sentence after the name: "cancelled **1× Lassi** on **T7**". */
  text: Part[]
  /** The sentence when nobody is recorded: "**T7** was checked out". */
  passive: Part[]
  sub?: string
  amountPaisa?: number
  badge?: { label: string; tone: 'neutral' | 'success' | 'warning' | 'danger' | 'primary' }
  link?: { to: string; label: string }
  /** Worth a manager's eye: a dish cancelled after it was sent, a missed guest, … */
  needsLook: boolean
  /** How takeaway, delivery and dine-in differ on the icon. */
  service?: OrderRow['service_type']
}

/** The last change to the business's payment QR, from its settings row. */
export interface PaymentQrChange {
  at: string
  by: string | null
  /** False when the change was removing it. */
  present: boolean
  label: string | null
}

/** That change as the settings row records it (the server stamps who and when), or null. */
export function paymentQrChange(
  settings:
    | {
        payment_qr: string | null
        payment_qr_label: string | null
        payment_qr_set_at?: string | null
        payment_qr_set_by?: string | null
      }
    | null
    | undefined,
): PaymentQrChange | null {
  if (!settings?.payment_qr_set_at) return null
  return {
    // Postgres and JavaScript print the same moment differently: compare like with like.
    at: new Date(settings.payment_qr_set_at).toISOString(),
    by: settings.payment_qr_set_by ?? null,
    present: !!settings.payment_qr,
    label: settings.payment_qr_label,
  }
}

const METHOD: Record<PaymentMethod, string> = {
  cash: 'cash',
  bank: 'bank',
  card: 'card',
  wallet: 'wallet',
  cheque: 'cheque',
  other: 'other',
}

const s = (strong: string): Part => ({ strong })

/** "9 min", "1 h 5 min": the time between two moments. */
export function minutesBetween(from: string, to: string): number {
  return Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 60_000))
}
export function minutesLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}

/** A guest answered within this many minutes counts as answered well. */
export const QUICK_ANSWER_MIN = 5

/**
 * One list, newest first. `from` / `to` are the period asked for: rows are fetched when
 * either end of something falls inside it, and only the events inside it are kept.
 * `restaurant` leaves a plain shop without kitchen wording; `nowMs` decides which waiting
 * requests have been missed.
 */
export function buildActivity(
  rows: ActivityRows,
  options: { from: string; to: string; nowMs: number; paymentQr?: PaymentQrChange | null },
): ActivityEvent[] {
  const { from, to, nowMs, paymentQr } = options
  const inside = (at: string | null | undefined): at is string => !!at && at >= from && at < to
  const out: ActivityEvent[] = []
  const tableName = new Map(rows.tables.map((t) => [t.id, t.name]))
  const orders = new Map([...rows.orders, ...rows.otherOrders].map((o) => [o.id, o]))
  const invoices = new Map([...rows.invoices, ...rows.otherInvoices].map((i) => [i.id, i]))
  const orderOfInvoice = new Map(
    [...orders.values()].filter((o) => o.invoice_id).map((o) => [o.invoice_id!, o]),
  )
  const nameOf = (order: OrderRow | undefined) =>
    order ? orderName(order, tableName.get(order.table_id ?? '') ?? null) : 'An order'
  const sent = new Set(rows.sentItemIds)
  const hadItems = new Set(rows.cancelledWithItems)
  const billLink = (invoice: InvoiceRow | undefined) =>
    invoice ? { to: `/invoices/${invoice.id}`, label: invoice.number ?? 'Open bill' } : undefined

  for (const order of rows.orders) {
    const name = nameOf(order)
    if (inside(order.opened_at)) {
      const guests =
        order.service_type === 'dine_in'
          ? ` for ${order.covers} ${order.covers === 1 ? 'guest' : 'guests'}`
          : ''
      const verb = order.service_type === 'dine_in' ? 'opened' : 'started'
      out.push({
        id: `open:${order.id}`,
        at: order.opened_at,
        group: 'orders',
        kind: 'order_opened',
        actorId: order.created_by,
        text: [`${verb} `, s(name), guests],
        passive: [s(name), ` was ${verb}${guests}`],
        link:
          order.status === 'open' || order.status === 'billed'
            ? { to: `/tables/${order.id}`, label: 'Open order' }
            : undefined,
        needsLook: false,
        service: order.service_type,
      })
    }
    if (!inside(order.closed_at)) continue
    if (order.status === 'void' && order.merged_into_id) {
      const into = nameOf(orders.get(order.merged_into_id))
      out.push({
        id: `merge:${order.id}`,
        at: order.closed_at,
        group: 'orders',
        kind: 'order_merged',
        actorId: null,
        text: ['merged ', s(name), ' into ', s(into)],
        passive: [s(name), ' was merged into ', s(into)],
        needsLook: false,
        service: order.service_type,
      })
    } else if (order.status === 'void') {
      const withItems = hadItems.has(order.id)
      out.push({
        id: `void:${order.id}`,
        at: order.closed_at,
        group: 'orders',
        kind: 'order_cancelled',
        actorId: null,
        text: ['cancelled the order on ', s(name)],
        passive: ['The order on ', s(name), ' was cancelled'],
        sub: [
          order.void_reason ? `Reason: ${order.void_reason}` : null,
          withItems ? 'it had dishes on it' : 'nothing was on it',
        ]
          .filter(Boolean)
          .join(' · '),
        badge: { label: 'Cancelled', tone: withItems ? 'warning' : 'neutral' },
        needsLook: withItems,
        service: order.service_type,
      })
    } else if (order.status === 'closed') {
      const invoice = order.invoice_id ? invoices.get(order.invoice_id) : undefined
      out.push({
        id: `paid:${order.id}`,
        at: order.closed_at,
        group: 'payments',
        kind: 'checked_out',
        actorId: invoice?.created_by ?? null,
        text: ['checked out ', s(name)],
        passive: [s(name), ' was checked out'],
        amountPaisa: invoice?.total_paisa,
        link: billLink(invoice),
        needsLook: false,
        service: order.service_type,
      })
    }
  }

  for (const row of rows.voids) {
    if (!inside(row.created_at)) continue
    const order = orders.get(row.order_id)
    const wasSent = !!row.void_of_id && sent.has(row.void_of_id)
    out.push({
      id: `dish:${row.id}`,
      at: row.created_at,
      group: 'orders',
      kind: 'dish_cancelled',
      actorId: row.created_by,
      text: [
        'cancelled ',
        s(`${formatQty(-row.qty_milli)}× ${row.description}`),
        ' on ',
        s(nameOf(order)),
      ],
      passive: [
        s(`${formatQty(-row.qty_milli)}× ${row.description}`),
        ' was cancelled on ',
        s(nameOf(order)),
      ],
      sub: [
        row.void_reason ? `Reason: ${row.void_reason}` : null,
        wasSent ? 'it had already gone to the kitchen' : 'before it was sent',
      ]
        .filter(Boolean)
        .join(' · '),
      amountPaisa: mulDivRound(row.unit_price_paisa, row.qty_milli, 1000),
      needsLook: wasSent,
      service: order?.service_type,
    })
  }

  for (const ticket of rows.tickets) {
    if (!inside(ticket.ready_at)) continue
    const order = orders.get(ticket.order_id)
    out.push({
      id: `ticket:${ticket.id}`,
      at: ticket.ready_at,
      group: 'kitchen',
      kind: 'ticket_ready',
      actorId: null,
      text: ['marked ', s(ticketName(ticket)), ' ready for ', s(nameOf(order))],
      passive: [s(ticketName(ticket)), ' was ready for ', s(nameOf(order))],
      sub: `Made in ${minutesLabel(minutesBetween(ticket.sent_at, ticket.ready_at))} · ${
        ticket.station === 'bar' ? 'bar' : 'kitchen'
      }`,
      badge: { label: 'Ready', tone: 'success' },
      needsLook: false,
    })
  }

  for (const request of rows.requests) {
    const where = tableName.get(request.table_id) ?? 'A table'
    const asked: Part[] =
      request.kind === 'call'
        ? [s(where), ' called the waiter']
        : request.kind === 'bill'
          ? [s(where), ' asked for the bill']
          : [s(where), ' sent an order from the table’s QR code']
    const base = {
      group: 'alerts' as const,
      actorId: null,
      text: asked,
      passive: asked,
      link: request.order_id
        ? { to: `/tables/${request.order_id}`, label: 'Open order' }
        : undefined,
    }
    if (request.status === 'pending') {
      if (!inside(request.created_at)) continue
      // One sent to a free table that nobody answered in time is gone for the guest.
      const missed =
        request.order_id === null && Date.parse(request.created_at) + GUEST_FREE_WAIT_MS < nowMs
      out.push({
        ...base,
        id: `request:${request.id}`,
        at: request.created_at,
        kind: missed ? 'request_missed' : 'request_waiting',
        sub: missed
          ? 'Nobody answered in time, so the guest was told to call a waiter'
          : 'Still waiting for someone',
        badge: missed
          ? { label: 'Missed', tone: 'warning' }
          : { label: 'Waiting', tone: 'primary' },
        needsLook: missed,
      })
      continue
    }
    if (!inside(request.created_at) && !inside(request.decided_at)) continue
    const took = request.decided_at ? minutesBetween(request.created_at, request.decided_at) : null
    out.push({
      ...base,
      id: `request:${request.id}`,
      at: request.created_at,
      kind: 'request_answered',
      actorId: request.status === 'cancelled' ? null : request.decided_by,
      sub:
        request.status === 'accepted'
          ? 'Answered'
          : request.status === 'rejected'
            ? 'Sent back'
            : 'Cancelled by the guest',
      badge:
        took === null || request.status === 'cancelled'
          ? undefined
          : {
              label: minutesLabel(took),
              tone: took <= QUICK_ANSWER_MIN ? 'success' : 'warning',
            },
      needsLook: false,
    })
  }

  for (const delivery of rows.deliveries) {
    const order = orders.get(delivery.order_id)
    const name = nameOf(order)
    const rider = delivery.rider_name ? ` with ${delivery.rider_name}` : ''
    const invoice = order?.invoice_id ? invoices.get(order.invoice_id) : undefined
    const base = { group: 'orders' as const, actorId: null, service: 'delivery' as const }
    if (inside(delivery.dispatched_at))
      out.push({
        ...base,
        id: `out:${delivery.id}`,
        at: delivery.dispatched_at,
        kind: 'delivery_out',
        text: ['sent out ', s(name), rider],
        passive: [s(name), ` left${rider}`],
        sub: delivery.address,
        amountPaisa: invoice?.total_paisa,
        needsLook: false,
      })
    if (inside(delivery.delivered_at))
      out.push({
        ...base,
        id: `delivered:${delivery.id}`,
        at: delivery.delivered_at,
        kind: 'delivered',
        text: ['delivered ', s(name)],
        passive: [s(name), ` was delivered${rider ? ` by ${delivery.rider_name}` : ''}`],
        badge: { label: 'Delivered', tone: 'success' },
        needsLook: false,
      })
    if (delivery.status === 'failed' && inside(delivery.updated_at))
      out.push({
        ...base,
        id: `failed:${delivery.id}`,
        at: delivery.updated_at,
        kind: 'delivery_failed',
        text: ['marked ', s(name), ' as failed'],
        passive: ['The delivery of ', s(name), ' failed'],
        sub: delivery.failed_reason ? `Reason: ${delivery.failed_reason}` : undefined,
        badge: { label: 'Failed', tone: 'danger' },
        link: billLink(invoice),
        needsLook: true,
      })
  }

  for (const invoice of rows.invoices) {
    const number = invoice.number ?? 'a bill'
    // A restaurant order's bill is told as its checkout, above.
    if (inside(invoice.finalized_at) && !orderOfInvoice.has(invoice.id))
      out.push({
        id: `bill:${invoice.id}`,
        at: invoice.finalized_at,
        group: 'payments',
        kind: 'bill_made',
        actorId: invoice.created_by,
        text: ['made bill ', s(number)],
        passive: ['Bill ', s(number), ' was made'],
        sub: invoice.customer_name ?? undefined,
        amountPaisa: invoice.total_paisa,
        link: billLink(invoice),
        needsLook: false,
      })
    if (invoice.status === 'cancelled' && inside(invoice.cancelled_at))
      out.push({
        id: `billcancel:${invoice.id}`,
        at: invoice.cancelled_at,
        group: 'payments',
        kind: 'bill_cancelled',
        actorId: null,
        text: ['cancelled bill ', s(number)],
        passive: ['Bill ', s(number), ' was cancelled'],
        sub: invoice.cancel_reason ? `Reason: ${invoice.cancel_reason}` : undefined,
        amountPaisa: -invoice.total_paisa,
        badge: { label: 'Cancelled', tone: 'warning' },
        link: billLink(invoice),
        needsLook: true,
      })
  }

  for (const note of rows.creditNotes) {
    if (!inside(note.finalized_at)) continue
    const invoice = invoices.get(note.invoice_id)
    out.push({
      id: `credit:${note.id}`,
      at: note.finalized_at,
      group: 'payments',
      kind: 'credit_note',
      actorId: note.created_by,
      text: ['made credit note ', s(note.number ?? 'a credit note')],
      passive: ['Credit note ', s(note.number ?? ''), ' was made'],
      sub: [invoice?.number ? `For ${invoice.number}` : null, note.reason]
        .filter(Boolean)
        .join(' · '),
      amountPaisa: -note.total_paisa,
      link: billLink(invoice),
      needsLook: false,
    })
  }

  // A copy of a bill (an original print is not news). A reprint of a paid bill is a common
  // way to fake a sale, so who printed it again is shown, but it is not flagged.
  for (const print of rows.prints) {
    if (print.copy_no < 1 || !inside(print.printed_at)) continue
    const invoice = invoices.get(print.invoice_id)
    out.push({
      id: `print:${print.id}`,
      at: print.printed_at,
      group: 'payments',
      kind: 'bill_reprinted',
      actorId: print.created_by,
      text: ['printed ', s(invoice?.number ?? 'a bill'), ' again'],
      passive: [s(invoice?.number ?? 'A bill'), ' was printed again'],
      sub: `Copy ${print.copy_no} · ${print.format === 'a4' ? 'A4' : 'Receipt'}`,
      link: billLink(invoice),
      needsLook: false,
    })
  }

  for (const payment of rows.payments) {
    if (!inside(payment.created_at)) continue
    const invoice = payment.invoice_id ? invoices.get(payment.invoice_id) : undefined
    // Money taken at the counter is part of that bill's own line.
    if (invoice?.finalized_at && minutesBetween(invoice.finalized_at, payment.created_at) < 1)
      continue
    const order = invoice ? orderOfInvoice.get(invoice.id) : undefined
    const forWhat: Part[] = order
      ? [' for ', s(nameOf(order))]
      : invoice?.number
        ? [' for ', s(invoice.number)]
        : []
    out.push({
      id: `payment:${payment.id}`,
      at: payment.created_at,
      group: 'payments',
      kind: 'payment',
      actorId: payment.created_by,
      text: ['took a payment', ...forWhat],
      passive: ['A payment came in', ...forWhat],
      sub: `Paid by ${METHOD[payment.method]}${payment.reference ? ` · ${payment.reference}` : ''}`,
      amountPaisa: payment.amount_paisa,
      link: billLink(invoice),
      needsLook: false,
    })
  }

  for (const receipt of rows.receipts) {
    if (!inside(receipt.created_at)) continue
    const supplier = receipt.supplier_name ?? 'a supplier'
    out.push({
      id: `receipt:${receipt.id}`,
      at: receipt.created_at,
      group: 'stock',
      kind: 'purchase',
      actorId: receipt.created_by,
      text: ['received stock from ', s(supplier)],
      passive: ['Stock came in from ', s(supplier)],
      sub: receipt.reference ?? undefined,
      link: { to: `/purchases/${receipt.id}`, label: 'Open purchase' },
      needsLook: false,
    })
  }

  for (const move of rows.adjustments) {
    if (!inside(move.occurred_at)) continue
    const product = move.product_name ?? 'a product'
    if (isBatchMove(move)) {
      // A move between batches is two rows that cancel out: told once, from the side it entered.
      if (move.qty_milli < 0) continue
      const moved = formatQty(move.qty_milli)
      out.push({
        id: `adjust:${move.id}`,
        at: move.occurred_at,
        group: 'stock',
        kind: 'stock_adjusted',
        actorId: move.created_by,
        text: ['moved ', s(product), ` between batches (${moved})`],
        passive: [s(product), ` was moved between batches (${moved})`],
        sub: move.notes ?? undefined,
        link: { to: `/products/${move.product_id}`, label: 'Open product' },
        needsLook: false,
      })
      continue
    }
    const by = `${move.qty_milli > 0 ? '+' : '−'}${formatQty(Math.abs(move.qty_milli))}`
    out.push({
      id: `adjust:${move.id}`,
      at: move.occurred_at,
      group: 'stock',
      kind: 'stock_adjusted',
      actorId: move.created_by,
      text: [move.type === 'count' ? 'counted ' : 'adjusted ', s(product), ` by ${by}`],
      passive: [s(product), ` was ${move.type === 'count' ? 'counted' : 'adjusted'} by ${by}`],
      sub: move.notes ?? undefined,
      link: { to: `/products/${move.product_id}`, label: 'Open product' },
      needsLook: false,
    })
  }

  // The business's payment QR decides where customers' money goes: its latest change
  // (the settings row keeps only that one) is always shown, with who made it.
  if (paymentQr && inside(paymentQr.at)) {
    const name: Part[] = paymentQr.label ? [' to ', s(paymentQr.label)] : []
    out.push({
      id: `payment-qr:${paymentQr.at}`,
      at: paymentQr.at,
      group: 'payments',
      kind: 'payment_qr',
      actorId: paymentQr.by,
      text: paymentQr.present ? ['set the payment QR', ...name] : ['removed the payment QR'],
      passive: paymentQr.present
        ? ['The payment QR was set', ...name]
        : ['The payment QR was removed'],
      sub: paymentQr.present
        ? 'Customers who pay by QR now pay this code. Check it is the right one.'
        : 'The checkout no longer shows a QR.',
      badge: { label: 'Payment QR', tone: 'warning' },
      link: { to: '/settings?tab=payments', label: 'Open Payments' },
      needsLook: false,
    })
  }

  return out.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : a.id.localeCompare(b.id)))
}

/** The numbers beside the list. */
export interface ActivitySummary {
  total: number
  needsLook: number
  dishesCancelledAfterSending: { count: number; paisa: number }
  ordersCancelled: number
  billsCancelled: number
  deliveriesFailed: number
  requests: { answered: number; missed: number; waiting: number; averageMin: number | null }
  /** How many things each person did, most first. */
  byActor: { actorId: string; count: number }[]
}

export function summarize(
  events: readonly ActivityEvent[],
  rows: Pick<ActivityRows, 'requests'>,
): ActivitySummary {
  const flaggedDishes = events.filter((e) => e.kind === 'dish_cancelled' && e.needsLook)
  const answered = rows.requests.filter((r) => r.status === 'accepted' && r.decided_at)
  const byActor = new Map<string, number>()
  for (const e of events) if (e.actorId) byActor.set(e.actorId, (byActor.get(e.actorId) ?? 0) + 1)
  return {
    total: events.length,
    needsLook: events.filter((e) => e.needsLook).length,
    dishesCancelledAfterSending: {
      count: flaggedDishes.length,
      paisa: flaggedDishes.reduce((sum, e) => sum - (e.amountPaisa ?? 0), 0),
    },
    ordersCancelled: events.filter((e) => e.kind === 'order_cancelled').length,
    billsCancelled: events.filter((e) => e.kind === 'bill_cancelled').length,
    deliveriesFailed: events.filter((e) => e.kind === 'delivery_failed').length,
    requests: {
      answered: events.filter((e) => e.kind === 'request_answered').length,
      missed: events.filter((e) => e.kind === 'request_missed').length,
      waiting: events.filter((e) => e.kind === 'request_waiting').length,
      averageMin:
        answered.length === 0
          ? null
          : Math.round(
              answered.reduce((sum, r) => sum + minutesBetween(r.created_at, r.decided_at!), 0) /
                answered.length,
            ),
    },
    byActor: [...byActor.entries()]
      .map(([actorId, count]) => ({ actorId, count }))
      .sort((a, b) => b.count - a.count),
  }
}

/** The start (local midnight) of the day `daysAgo` days before `now`, as ISO. */
export function dayStart(now: Date, daysAgo: number): string {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - daysAgo)
  return d.toISOString()
}

export type ActivityPeriod = 'today' | 'yesterday' | 'week'

/** The stretch a period covers; "week" is today and the six days before it. */
export function periodRange(period: ActivityPeriod, now: Date): { from: string; to: string } {
  if (period === 'yesterday') return { from: dayStart(now, 1), to: dayStart(now, 0) }
  return { from: dayStart(now, period === 'week' ? 6 : 0), to: dayStart(now, -1) }
}
